import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as fragments from '~/apollo/fragments';
import type {
  StudyTrainingImagesRouteQuery,
  StudyTrainingImagesRouteQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { useStudyOutletContext } from '~/routes/study/$studyId';

const STUDY_TRAINING_IMAGES_ROUTE = gql`
  query StudyTrainingImagesRoute($studyId: Int!) {
    studyList(id: $studyId) {
      ...studyParts
      trainingImages {
        ...trainingImageParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
          }
        }
        files {
          ...fileParts
        }
      }
    }
  }

  ${fragments.studyParts}
  ${fragments.trainingImageParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
`;

export default function StudyTrainingImagesRoute() {
  const ctx = useStudyOutletContext();
  const studyId = ctx.study.id;

  const { data, loading } = useQuery<
    StudyTrainingImagesRouteQuery,
    StudyTrainingImagesRouteQueryVariables
  >(STUDY_TRAINING_IMAGES_ROUTE, { variables: { studyId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: STUDY_TRAINING_IMAGES_ROUTE, variables: { studyId } },
  ];

  const items =
    data?.studyList.find(s => s.id === studyId)?.trainingImages ?? [];

  if (loading) return <SpinnerPlaceholder />;

  return (
    <SupportingObjectList
      items={items}
      soParentType="study"
      soParentId={studyId}
      bookmarkParentType={BookmarkParentType.TrainingImage}
      refetchQueries={refetchQueries}
    />
  );
}
